import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { TextField, InputAdornment, MenuItem } from "@mui/material";
import {
  Search as SearchIcon,
  Close,
  Flag,
  Visibility,
  PushPin,
  PushPinOutlined,
} from "@mui/icons-material";
import moment from "moment";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CommunityAdminService from "../../services/CommunityAdminService.ts";

// Matches the toolbar used in AddEditBlog.tsx, so admin post editing has the
// same rich-text capability as blog post editing.
const QUILL_MODULES = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"],
    ["blockquote", "code-block"],
    [{ color: [] }, { background: [] }],
    ["link", "image", "video"],
  ],
};

type Tab = "all" | "reported";

const POST_TYPE_COLORS: Record<string, string> = {
  ARTICLE: "#1976d2",
  COLLECTION: "#7b1fa2",
  EVENT: "#c62828",
  DISCUSSION: "#388e3c",
};

const isPostPinned = (post: any) =>
  !!post?.pinnedUntil && new Date(post.pinnedUntil).getTime() > Date.now();

const REASON_LABELS: Record<string, string> = {
  spam: "Spam",
  harassment: "Harassment",
  inappropriate: "Inappropriate content",
  misinformation: "Misinformation",
  other: "Other",
};

// ─── Post detail modal ─────────────────────────────────────────────────────────

const PostDetailModal: React.FC<{
  postId: string;
  onClose: () => void;
  onChanged: () => void;
}> = ({ postId, onClose, onChanged }) => {
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [pinDays, setPinDays] = useState(3);
  const [pinning, setPinning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await CommunityAdminService.getPostDetail(postId);
      if (res.status) {
        setPost(res.data);
        setTitle(res.data.title || "");
        setContent(res.data.content || "");
      }
    } catch {
      toast.error("Failed to load post");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [postId]);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const res = await CommunityAdminService.updatePost(postId, {
        title,
        content,
      });
      if (res.status) {
        toast.success("Post updated");
        setEditing(false);
        load();
        onChanged();
      }
    } catch {
      toast.error("Failed to update post");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (
      !window.confirm(
        "Remove this post? It will be hidden from the public feed.",
      )
    )
      return;
    try {
      const res = await CommunityAdminService.removePost(postId);
      if (res.status) {
        toast.success("Post removed");
        load();
        onChanged();
      }
    } catch {
      toast.error("Failed to remove post");
    }
  };

  const handleRestore = async () => {
    try {
      const res = await CommunityAdminService.restorePost(postId);
      if (res.status) {
        toast.success("Post restored");
        load();
        onChanged();
      }
    } catch {
      toast.error("Failed to restore post");
    }
  };

  const handlePin = async () => {
    setPinning(true);
    try {
      const res = await CommunityAdminService.pinPost(postId, pinDays);
      if (res.status) {
        toast.success(res.message || "Post pinned");
        load();
        onChanged();
      } else {
        toast.error(res.error || "Failed to pin post");
      }
    } catch {
      toast.error("Failed to pin post");
    } finally {
      setPinning(false);
    }
  };

  const handleUnpin = async () => {
    setPinning(true);
    try {
      const res = await CommunityAdminService.unpinPost(postId);
      if (res.status) {
        toast.success("Post unpinned");
        load();
        onChanged();
      }
    } catch {
      toast.error("Failed to unpin post");
    } finally {
      setPinning(false);
    }
  };

  const handleDeleteForever = async () => {
    if (!window.confirm("Permanently delete this post? This cannot be undone."))
      return;
    try {
      const res = await CommunityAdminService.deletePost(postId);
      if (res.status) {
        toast.success("Post permanently deleted");
        onChanged();
        onClose();
      }
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const handleResolveReport = async (
    reportId: string,
    action: "remove_post" | "dismiss",
  ) => {
    try {
      const res = await CommunityAdminService.resolveReport(
        postId,
        reportId,
        action,
      );
      if (res.status) {
        toast.success(res.message);
        load();
        onChanged();
      }
    } catch {
      toast.error("Failed to resolve report");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        overflowY: "auto",
        padding: "40px 20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "100%",
          maxWidth: 720,
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "16px 22px",
            borderBottom: "1px solid #eee",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h5 className="fw-bold mb-0">Post Detail</h5>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <Close />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5">Loading…</div>
        ) : !post ? (
          <div className="text-center py-5 text-muted">Post not found</div>
        ) : (
          <div style={{ padding: 22 }}>
            {/* Meta row */}
            <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
              <span
                style={{
                  background: POST_TYPE_COLORS[post.postType] || "#555",
                  color: "#fff",
                  padding: "2px 9px",
                  borderRadius: 5,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              >
                {post.postType}
              </span>
              {post.removedByAdmin && (
                <span
                  style={{
                    background: "#c62828",
                    color: "#fff",
                    padding: "2px 9px",
                    borderRadius: 5,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                  }}
                >
                  REMOVED
                </span>
              )}
              {isPostPinned(post) && (
                <span
                  style={{
                    background: "#FFD600",
                    color: "#000",
                    padding: "2px 9px",
                    borderRadius: 5,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  <PushPin style={{ fontSize: 13 }} /> Pinned until{" "}
                  {moment(post.pinnedUntil).format("MMM D, h:mm A")}
                </span>
              )}
              <span className="text-muted small">
                by {post.author?.user_name || "Unknown"} ·{" "}
                {moment(post.createdAt).format("MMM D, YYYY h:mm A")}
              </span>
              {post.communityId?.name && (
                <span className="badge bg-light text-dark border">
                  {post.communityId.name}
                </span>
              )}
              {post.linkedCollection?.name && (
                <span className="badge bg-light text-dark border">
                  Collection: {post.linkedCollection.name}
                </span>
              )}
            </div>

            {/* Title / content */}
            {editing ? (
              <div className="mb-3">
                <input
                  className="form-control mb-2 fw-bold"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <ReactQuill
                  value={content}
                  onChange={setContent}
                  modules={QUILL_MODULES}
                  style={{ height: "260px", marginBottom: "50px" }}
                />
                <div className="d-flex justify-content-end gap-2 mt-2">
                  <button
                    className="btn btn-sm btn-light border"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{
                      background: "#FFD600",
                      fontWeight: 700,
                      border: "2px solid #000",
                    }}
                    onClick={handleSaveEdit}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-3">
                <h5 className="fw-bold">{post.title}</h5>
                {post.content && (
                  <div
                    className="text-muted"
                    style={{ fontSize: "0.9rem", lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                )}
                {post.images?.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {post.images.map((img: string, i: number) => (
                      <img
                        key={i}
                        src={img}
                        alt=""
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Reports section */}
            {post.reports?.length > 0 && (
              <div
                className="mb-3"
                style={{
                  background: "#fff8e1",
                  border: "1px solid #ffe082",
                  borderRadius: 10,
                  padding: 14,
                }}
              >
                <div className="fw-bold small mb-2 d-flex align-items-center gap-1">
                  <Flag fontSize="small" style={{ color: "#e65100" }} /> Reports
                  ({post.reports.length})
                </div>
                {post.reports.map((r: any) => (
                  <div
                    key={r._id}
                    className="d-flex align-items-center justify-content-between py-1"
                    style={{ borderBottom: "1px solid #ffe082" }}
                  >
                    <div>
                      <span className="fw-600 small">
                        {REASON_LABELS[r.reason] || r.reason}
                      </span>
                      {r.details && (
                        <span className="text-muted small ms-2">
                          — {r.details}
                        </span>
                      )}
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.7rem" }}
                      >
                        by {r.reporter?.user_name || "Unknown"} ·{" "}
                        {moment(r.createdAt).format("MMM D")} ·{" "}
                        <span style={{ textTransform: "capitalize" }}>
                          {r.status}
                        </span>
                      </div>
                    </div>
                    {r.status === "pending" && (
                      <div className="d-flex gap-1 flex-shrink-0">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                          onClick={() => handleResolveReport(r._id, "dismiss")}
                        >
                          Dismiss
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          style={{ fontSize: "0.72rem", padding: "2px 8px" }}
                          onClick={() =>
                            handleResolveReport(r._id, "remove_post")
                          }
                        >
                          Remove post
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Comments */}
            <div className="mb-3">
              <div className="fw-bold small mb-2">
                Comments ({post.commentCount || 0})
              </div>
              {post.comments?.length > 0 ? (
                <div style={{ maxHeight: 220, overflowY: "auto" }}>
                  {post.comments.map((c: any) => (
                    <div
                      key={c._id}
                      className="d-flex gap-2 py-2"
                      style={{ borderBottom: "1px solid #f0f0f0" }}
                    >
                      {c.userId?.profile_pic ? (
                        <img
                          src={c.userId.profile_pic}
                          alt=""
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            background: "#eee",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <div>
                        <span className="fw-600 small">
                          {c.userId?.user_name || "Unknown"}
                        </span>
                        <p className="mb-0 small text-muted">{c.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted small">No comments yet.</p>
              )}
            </div>

            {/* Pin to top of feed */}
            <div
              className="mb-3 d-flex align-items-center flex-wrap gap-2"
              style={{
                background: "#fffdf2",
                border: "1px solid #ffe082",
                borderRadius: 10,
                padding: "10px 14px",
              }}
            >
              {isPostPinned(post) ? (
                <>
                  <PushPin fontSize="small" style={{ color: "#e65100" }} />
                  <span className="small fw-600">
                    Pinned until{" "}
                    {moment(post.pinnedUntil).format("MMM D, YYYY h:mm A")}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary ms-auto"
                    onClick={handleUnpin}
                    disabled={pinning}
                  >
                    {pinning ? "Unpinning…" : "Unpin"}
                  </button>
                </>
              ) : (
                <>
                  <PushPinOutlined fontSize="small" style={{ color: "#999" }} />
                  <span className="small fw-600">Pin to top of feed for</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 90 }}
                    value={pinDays}
                    onChange={(e) => setPinDays(Number(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                      <option key={d} value={d}>
                        {d} day{d === 1 ? "" : "s"}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-sm ms-auto"
                    style={{
                      background: "#FFD600",
                      fontWeight: 700,
                      border: "2px solid #000",
                    }}
                    onClick={handlePin}
                    disabled={pinning}
                  >
                    {pinning ? "Pinning…" : "Pin"}
                  </button>
                </>
              )}
            </div>

            {/* Actions */}
            <div
              className="d-flex flex-wrap gap-2 pt-2"
              style={{ borderTop: "1px solid #eee" }}
            >
              {!editing && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setEditing(true)}
                >
                  Edit
                </button>
              )}
              {post.removedByAdmin ? (
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={handleRestore}
                >
                  Restore
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleRemove}
                >
                  Remove (soft)
                </button>
              )}
              <button
                className="btn btn-sm btn-danger"
                onClick={handleDeleteForever}
              >
                Delete permanently
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────────

const PostsReportsManager: React.FC<{ user?: any }> = () => {
  const [tab, setTab] = useState<Tab>("reported");
  const [posts, setPosts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [postType, setPostType] = useState("");
  const [published, setPublished] = useState("");
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res =
        tab === "reported"
          ? await CommunityAdminService.listReportedPosts(1, 50)
          : await CommunityAdminService.listPosts({
              search,
              postType,
              published: published as any,
              page: 1,
              limit: 50,
            });
      if (res.status) {
        setPosts(res.data);
        setTotal(res.pagination.total);
      }
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [tab]);

  const applyFilters = () => fetchAll();

  return (
    <div className="container-fluid" style={{ maxWidth: 1100, paddingTop: 8 }}>
      <h3 className="fw-bold mb-4">Posts &amp; Reports</h3>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {(["reported", "all"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: tab === t ? "2px solid #000" : "1px solid #ccc",
              background: tab === t ? "#FFD600" : "#fff",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {t === "reported" && <Flag fontSize="small" />}
            {t === "reported" ? "Reported Posts" : "All Posts"}
          </button>
        ))}
      </div>

      {/* Filters — only for "all" tab */}
      {tab === "all" && (
        <div className="row g-2 mb-4">
          <div className="col-md-4">
            <TextField
              fullWidth
              size="small"
              placeholder="Search title or content…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="col-md-3">
            <TextField
              fullWidth
              size="small"
              select
              label="Type"
              value={postType}
              onChange={(e) => setPostType(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="DISCUSSION">Discussion</MenuItem>
              <MenuItem value="ARTICLE">Article</MenuItem>
              <MenuItem value="COLLECTION">Collection</MenuItem>
              <MenuItem value="EVENT">Event</MenuItem>
            </TextField>
          </div>
          <div className="col-md-3">
            <TextField
              fullWidth
              size="small"
              select
              label="Status"
              value={published}
              onChange={(e) => setPublished(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="true">Published</MenuItem>
              <MenuItem value="false">Draft</MenuItem>
            </TextField>
          </div>
          <div className="col-md-2 d-grid">
            <button
              onClick={applyFilters}
              style={{
                background: "#FFD600",
                fontWeight: 700,
                border: "2px solid #000",
                borderRadius: 8,
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      <div className="mb-3">
        <span className="text-muted small fw-600">
          {tab === "reported"
            ? `${total} post(s) with pending reports`
            : `${total} total posts`}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">Loading…</div>
      ) : posts.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover user-management-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>Author</th>
                {tab === "reported" ? <th>Reports</th> : <th>Status</th>}
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post._id}>
                  <td>
                    <span
                      style={{
                        background: POST_TYPE_COLORS[post.postType] || "#555",
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: 5,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                      }}
                    >
                      {post.postType}
                    </span>
                  </td>
                  <td>
                    <div
                      className="fw-600"
                      style={{
                        maxWidth: 320,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isPostPinned(post) && (
                        <PushPin
                          style={{ fontSize: 14, color: "#e65100" }}
                          titleAccess="Pinned"
                        />
                      )}
                      {post.title}
                    </div>
                    {post.removedByAdmin && (
                      <span className="text-danger small fw-600">Removed</span>
                    )}
                  </td>
                  <td className="small">
                    {post.author?.user_name || "Unknown"}
                  </td>
                  {tab === "reported" ? (
                    <td>
                      <span className="badge bg-danger">
                        {post.pendingReports?.length || post.reportCount}{" "}
                        pending
                      </span>
                    </td>
                  ) : (
                    <td>
                      <span
                        className={`badge ${post.published ? "bg-success" : "bg-secondary"}`}
                      >
                        {post.published ? "Published" : "Draft"}
                      </span>
                    </td>
                  )}
                  <td className="small text-muted">
                    {moment(post.createdAt).format("MMM D, YYYY")}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-sm btn-link p-0 fw-bold d-inline-flex align-items-center gap-1"
                      onClick={() => setSelectedPostId(post._id)}
                    >
                      <Visibility fontSize="small" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <p className="fw-bold">
            {tab === "reported" ? "No pending reports 🎉" : "No posts found"}
          </p>
        </div>
      )}

      {selectedPostId && (
        <PostDetailModal
          postId={selectedPostId}
          onClose={() => setSelectedPostId(null)}
          onChanged={fetchAll}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({ user: state.auth.user });
export default connect(mapStateToProps)(PostsReportsManager);
